import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { useToast } from './ui/use-toast';
import { api, endpoints } from '../services/api';
import { Upload, Download, Trash2, FileText, File, FileImage, ExternalLink } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const EK3_MAX_SIZE = 5 * 1024 * 1024; // 5MB

const Ek3DocumentUpload = ({ caseId, currentDocument, onDocumentChange, readOnly = false }) => {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [openingLink, setOpeningLink] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('document', file);
      return api.post(endpoints.cases.uploadEk3Document(caseId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (response) => {
      toast({ title: 'Başarılı', description: 'EK-3 belgesi başarıyla yüklendi', variant: 'success' });
      queryClient.invalidateQueries(['case', caseId]);
      queryClient.invalidateQueries(['cases']);
      if (onDocumentChange) onDocumentChange(response.data.data);
    },
    onError: (error) => {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'EK-3 belgesi yüklenirken hata oluştu',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(endpoints.cases.deleteEk3Document(caseId)),
    onSuccess: () => {
      toast({ title: 'Başarılı', description: 'EK-3 belgesi başarıyla silindi', variant: 'success' });
      queryClient.invalidateQueries(['case', caseId]);
      queryClient.invalidateQueries(['cases']);
      if (onDocumentChange) onDocumentChange(null);
    },
    onError: (error) => {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'EK-3 belgesi silinirken hata oluştu',
        variant: 'destructive',
      });
    },
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = '';

    if (file.size > EK3_MAX_SIZE) {
      toast({ title: 'Hata', description: 'Dosya boyutu 5MB\'dan büyük olamaz', variant: 'destructive' });
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Hata', description: 'Sadece PDF, JPG, PNG, DOC, DOCX dosyaları yüklenebilir', variant: 'destructive' });
      return;
    }

    uploadMutation.mutate(file);
  };

  const handleDownload = async () => {
    if (!currentDocument?.ek3_document_filename) return;
    setDownloading(true);
    try {
      const response = await api.get(endpoints.cases.getEk3Document(caseId), { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentDocument.ek3_document_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Hata', description: 'EK-3 belgesi indirilirken hata oluştu', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenLink = async () => {
    if (!currentDocument?.ek3_document_filename) return;
    setOpeningLink(true);
    try {
      const response = await api.get(endpoints.cases.getEk3Document(caseId), { responseType: 'blob' });
      const contentType = getMimeFromFilename(currentDocument.ek3_document_filename);
      const dataBlob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const arrayBuffer = await dataBlob.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      toast({ title: 'Hata', description: 'EK-3 belgesi açılırken hata oluştu', variant: 'destructive' });
    } finally {
      setOpeningLink(false);
    }
  };

  const handleDelete = () => {
    if (!currentDocument?.ek3_document_filename) return;
    if (window.confirm(`"${currentDocument.ek3_document_filename}" EK-3 belgesini silmek istediğinizden emin misiniz?`)) {
      deleteMutation.mutate();
    }
  };

  const getMimeFromFilename = (filename) => {
    const ext = (filename || '').split('.').pop()?.toLowerCase();
    const mime = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };
    return mime[ext] || 'application/octet-stream';
  };

  const getFileIcon = (filename) => {
    if (!filename) return <File className="h-5 w-5" />;
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <FileText className="h-5 w-5 text-red-500" />;
    if (['jpg', 'jpeg', 'png'].includes(ext)) return <FileImage className="h-5 w-5 text-blue-500" />;
    if (['doc', 'docx'].includes(ext)) return <FileText className="h-5 w-5 text-blue-600" />;
    return <File className="h-5 w-5" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`;
  };

  const hasDocument = !!currentDocument?.ek3_document_filename;

  return (
    <div className="space-y-3">
      <div className="border-2 border-dashed border-orange-200 rounded-lg p-5 bg-orange-50/30 text-center">
        {hasDocument ? (
          <div className="space-y-3">
            <div className="flex items-center justify-center space-x-3">
              {getFileIcon(currentDocument.ek3_document_filename)}
              <div className="text-left">
                <p className="font-medium text-sm">{currentDocument.ek3_document_filename}</p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(currentDocument.ek3_document_size)} •{' '}
                  Yükleme:{' '}
                  {new Date(currentDocument.ek3_document_upload_date).toLocaleDateString('tr-TR')}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Button type="button" variant="outline" size="sm" onClick={handleDownload} disabled={downloading}>
                <Download className="h-4 w-4 mr-1" />
                {downloading ? 'İndiriliyor...' : 'İndir'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenLink}
                disabled={openingLink}
                className="text-blue-600 hover:text-blue-700"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                {openingLink ? 'Açılıyor...' : 'Link'}
              </Button>
              {!readOnly && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadMutation.isLoading}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Değiştir
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    disabled={deleteMutation.isLoading}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Sil
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : readOnly ? (
          <div className="space-y-2 text-gray-400">
            <File className="h-10 w-10 mx-auto" />
            <p className="text-sm">EK-3 belgesi henüz yüklenmemiş</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className="h-10 w-10 mx-auto text-orange-400" />
            <div>
              <p className="text-sm font-medium">EK-3 Belgesi Yükle</p>
              <p className="text-xs text-gray-500">PDF, JPG, PNG, DOC, DOCX (Maks. 5MB)</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isLoading}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <Upload className="h-4 w-4 mr-1" />
              {uploadMutation.isLoading ? 'Yükleniyor...' : 'Dosya Seç'}
            </Button>
          </div>
        )}
      </div>
      {!readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onChange={handleFileSelect}
          className="hidden"
        />
      )}
      <p className="text-xs text-orange-600 text-center">
        EK-3 protokol belgesi, başkanlık onay formu gibi ilgili dokümanları yükleyebilirsiniz.
      </p>
    </div>
  );
};

export default Ek3DocumentUpload;
